// ─── Card Types & Constants (Moor200) ────────────────────────────────────────

import { z } from "zod";

/** Suits in the game */
export const SUITS = ["hearts", "diamonds", "clubs", "spades"] as const;
export type Suit = (typeof SUITS)[number];

/**
 * Ranks in canonical order for sequences.
 * A=1, 3,4,5,6,7,8,9,10,J=11,Q=12,K=13, 2=natural joker position
 * Note: Rank "2" is an in-suit natural joker (A-2-3 position, same suit).
 * "joker" rank = actual deck Joker cards (always substitute).
 */
export const RANKS = [
  "A",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "J",
  "Q",
  "K",
  "2",
  "joker", // actual deck Joker card
] as const;
export type Rank = (typeof RANKS)[number];

/** Numeric value mapping for sequence ordering (Ace-low) */
export const RANK_ORDER: Record<string, number> = {
  A: 1,
  "3": 3,
  "4": 4,
  "5": 5,
  "6": 6,
  "7": 7,
  "8": 8,
  "9": 9,
  "10": 10,
  J: 11,
  Q: 12,
  K: 13,
  "2": 14, // natural joker position in sequence
  joker: 15, // deck joker has no fixed position
};

/** All sequence ranks (excluding jokers) in order */
export const SEQUENCE_RANKS = [
  "A",
  "3",
  "4",
  "5",
  "6",
  "7",
  "8",
  "9",
  "10",
  "J",
  "Q",
  "K",
] as const;
export type SequenceRank = (typeof SEQUENCE_RANKS)[number];

/**
 * Sequence rank index (0-based) for ordering.
 * Full sequence: A(0), 2/natural-joker(1), 3(2), 4(3), ..., K(12), A-high(13)
 */
export const SEQ_RANK_INDEX: Record<string, number> = {
  A: 0,
  "2": 1,
  "3": 2,
  "4": 3,
  "5": 4,
  "6": 5,
  "7": 6,
  "8": 7,
  "9": 8,
  "10": 9,
  J: 10,
  Q: 11,
  K: 12,
};

export interface Card {
  id: string;
  deckIndex: number;
  suit: Suit;
  rank: Rank;
  isJoker: boolean;     // true for rank "2" (natural) OR deck joker
  isDeckJoker: boolean; // true ONLY for actual deck Joker cards
}

export const CardSchema = z.object({
  id: z.string(),
  deckIndex: z.number().int().min(0).max(1),
  suit: z.enum(SUITS),
  rank: z.enum(RANKS),
  isJoker: z.boolean(),
  isDeckJoker: z.boolean(),
});

// ─── Meld Types ─────────────────────────────────────────────────────────────

/**
 * Meld types for Moor200 (sequences only):
 * - sequence:  valid same-suit consecutive sequence (any length >= 3, no Moor bonus)
 * - moor100:   7+ card sequence with at least one substitute joker
 * - moor200:   7-13 cards, fully natural (no substitute jokers)
 * - moor1000:  14 cards, A-2-3-..K-A, fully natural
 */
export type MeldType =
  | "sequence"
  | "moor100"
  | "moor200"
  | "moor1000";

export interface Meld {
  id: string;
  teamId: "A" | "B";
  cards: Card[];
  meldType: MeldType;
  suit?: Suit;
  basePoints: number;
}

// ─── Player & Team ──────────────────────────────────────────────────────────

export type TeamId = "A" | "B";
export type SeatIndex = 0 | 1 | 2 | 3;

export function getTeamForSeat(seat: SeatIndex): TeamId {
  return seat % 2 === 0 ? "A" : "B";
}

export function getPartnerSeat(seat: SeatIndex): SeatIndex {
  return ((seat + 2) % 4) as SeatIndex;
}

export function getNextSeat(seat: SeatIndex): SeatIndex {
  return ((seat + 1) % 4) as SeatIndex;
}

export interface PlayerState {
  sessionId: string;
  displayName: string;
  seatIndex: SeatIndex;
  teamId: TeamId;
  hand: Card[];
  isConnected: boolean;
  isBot?: boolean;
}

// ─── Game Phase ─────────────────────────────────────────────────────────────

export type GamePhase =
  | "lobby"
  | "dealing"
  | "playing"
  | "choosing-side-pile"
  | "round-end"
  | "game-over";

// ─── Turn Actions ────────────────────────────────────────────────────────────

export const TurnActionSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("draw") }),
  z.object({
    type: z.literal("take-center"),
    /** Card IDs to form the immediate meld (must include top discard card).
     *  When isInitialCenterCard is true, pass only [topCard.id] — no meld required. */
    cardIds: z.array(z.string()).min(1),
  }),
  z.object({
    type: z.literal("create-meld"),
    cardIds: z.array(z.string()).min(3),
  }),
  z.object({
    type: z.literal("add-to-meld"),
    cardIds: z.array(z.string()).min(1),
    meldId: z.string(),
  }),
  z.object({
    type: z.literal("discard"),
    cardId: z.string(),
  }),
  z.object({ type: z.literal("end-turn") }),
  z.object({
    type: z.literal("choose-side-pile"),
    pile: z.enum(["A", "B"]),
  }),
]);

export type TurnAction = z.infer<typeof TurnActionSchema>;

// ─── Turn Phase ──────────────────────────────────────────────────────────────

export type TurnPhase =
  | "start"
  | "drawn"
  | "must-discard"
  | "took-initial-card"; // took first card of round — must discard replacement, then draw+discard

// ─── Score Structures ────────────────────────────────────────────────────────

export interface RoundScore {
  roundNumber: number;
  teamAScore: number;
  teamBScore: number;
  teamADetails: ScoreBreakdown;
  teamBDetails: ScoreBreakdown;
}

export interface ScoreBreakdown {
  meldBonusPoints: number;        // Sum of Moor100/200/1000 bonuses
  meldCardPoints: number;         // Sum of individual card point values on table
  cardsInHandPenalty: number;     // Negative of hand card point values
  openedBonus: number;            // +50 if this team opened
  sidePileNotTakenPenalty: number; // -100 if team didn't take the side pile (الـ11)
  noMoor200Penalty: number;        // -200 if team has no Moor200/Moor1000 on table
  total: number;
}

export interface GameState {
  roomId: string;
  phase: GamePhase;
  players: PlayerState[];
  currentTurnSeat: SeatIndex;
  turnPhase: TurnPhase;
  roundNumber: number;
  roundStartSeat: SeatIndex;

  drawPile: Card[];
  centerDiscard: Card[];  // Full pile; only TOP card sent to clients
  sidePileA: Card[];
  sidePileB: Card[];
  sidePileATaken: boolean;
  sidePileBTaken: boolean;
  sidePileATeam: TeamId | null;
  sidePileBTeam: TeamId | null;

  melds: Meld[];

  teamATotalScore: number;
  teamBTotalScore: number;
  roundScores: RoundScore[];

  teamATookSidePile: boolean;
  teamBTookSidePile: boolean;

  hasDrawnThisTurn: boolean;
  hasTakenCenterThisTurn: boolean;
  hasMeldedThisTurn: boolean;
  /** True once the player has laid their first NATURAL sequence this turn.
   *  Required before joker-containing sequences are allowed in the SAME turn. */
  hasMeldedNaturalThisTurn: boolean;
  /** Seat indices that have already opened (played their first natural meld in a
   *  previous turn). Players in this list have no restrictions in later turns. */
  openedSeats: number[];

  tookCenterCount: number;
  isInitialCenterCard: boolean;

  /** IDs of melds created during the current turn (reset on advanceTurn).
   *  Used to enforce the natural-only add restriction on newly created melds. */
  meldsCreatedThisTurn: string[];

  /** Set to the seat that drew the last card from the draw pile.
   *  The NEXT player gets one "grace turn" (can take center / meld / discard).
   *  Round ends when that player discards or ends their turn. */
  drawPileExhaustedSeat: SeatIndex | null;

  seed: number;
  winner: TeamId | null;
}

// ─── Config ──────────────────────────────────────────────────────────────────

export const GAME_CONFIG = {
  TOTAL_CARDS: 108,   // 52×2 regular + 4 deck jokers
  CARDS_PER_PLAYER: 11,
  SIDE_PILE_SIZE: 11,
  MIN_MELD_SIZE: 3,
  MOOR_SIZE: 7,           // Minimum cards for a Moor bonus
  WIN_SCORE: 3200,
  MOOR100_BONUS: 100,
  MOOR200_BONUS: 200,
  MOOR1000_BONUS: 1000,
  OPENED_BONUS: 100,            // Bonus for the team that opens (empties hand)
  SIDE_PILE_NOT_TAKEN_PENALTY: 100, // Penalty if team didn't claim the side pile
  NO_MOOR200_PENALTY: 200,       // Penalty if team has no Moor200/Moor1000 on table

  CARD_POINTS_LOW: 5,     // Ranks 3-9
  CARD_POINTS_HIGH: 10,   // Ranks 10, J, Q, K, A
  CARD_POINTS_JOKER: 20,  // Rank "2" (natural joker) or deck Joker
} as const;

/** Returns the point value of a single card */
export function cardPointValue(card: Card): number {
  if (card.isJoker) return GAME_CONFIG.CARD_POINTS_JOKER;
  if (["10", "J", "Q", "K", "A"].includes(card.rank)) return GAME_CONFIG.CARD_POINTS_HIGH;
  return GAME_CONFIG.CARD_POINTS_LOW; // 3-9
}
